import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserAssignments } from "../redux/slices/users";
import {
    Typography,
    Box,
    useTheme,
    Container,
    Autocomplete,
    TextField,
    IconButton,
    Tabs,
    Tab,
    Chip,
    Button,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import {
    Bolt,
    KeyboardArrowUp,
    KeyboardArrowDown,
    Person,
    AccessTime,
    Warning as WarningIcon,
    Today,
    Assignment as AssignmentIcon,
    Folder as FolderIcon,
    Business as BusinessIcon,
    Refresh,
} from "@mui/icons-material";
import styled from "@emotion/styled";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useHasPermission } from "../utils/permissionUtils";
import { accountPath, clientPath, projectPath } from "../utils/navigateUtils";
import PageHeader from "../components/PageHeader";
import { getReportUsers } from "../redux/ducks/reports";
import { getRunningTimerByUser } from "../redux/ducks/time";
import RunningTimer from "../components/RunningTimer";
import TimerButton from "../components/TimerButton";
import StatCard from "../components/StatCard";
import { formatDate, formatDueLabel } from "../utils/dateUtils";
import {
    URGENCY_ORDER,
    URGENCY_META,
    buildStats,
    bucketTasksByUrgency,
    dueTone,
    groupProjectsByAccount,
    groupTasksUnderProjects,
    isResponsible,
    sortAssignments,
    splitByRelation,
    splitByType,
} from "../utils/assignmentUtils";


const ContainerStyled = styled(Container)(({ theme }) => ({
    padding: theme.spacing(3, 2),
    width: "100%",
    marginRight: "auto",
    marginLeft: "auto",
}));

const HeaderUserAutocomplete = styled(Autocomplete)(({ theme }) => ({
    width: 280,
    [theme.breakpoints.down("sm")]: { width: "100%" },
}));


const StatStrip = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
}));


const StyledTabs = styled(Tabs)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
}));

const TabLabelBox = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: 6,
});

const TabCountChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(2),
    fontSize: theme.typography.size.xs,
}));

const TabIntro = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    maxWidth: "68ch",
}));


const HeroCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$active",
})(({ theme, $active }) => ({
    position: "relative",
    padding: theme.spacing(2.5, 3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    borderLeft: `4px solid ${$active ? theme.palette.primary.main : theme.palette.warning.main}`,
}));

const HeroEyebrow = styled(Box)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
}));

const HeroDot = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$active",
})(({ theme, $active }) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: $active ? theme.palette.primary.main : theme.palette.warning.main,
    boxShadow: $active ? `0 0 0 4px ${theme.palette.primary.main}22` : "none",
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    cursor: "pointer",
    color: theme.palette.text.primary,
    "&:hover": {
        color: theme.palette.primary.main,
        textDecoration: "underline",
    },
    marginBottom: theme.spacing(0.75),
    lineHeight: 1.3,
}));

const HeroMetaRow = styled(Box)(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.5, 2),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.smd,
    "& > span": {
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
    },
    "& .MuiSvgIcon-root": { fontSize: theme.typography.size.base },
}));

const HeroActions = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
}));

const HeroRunningClock = styled(Box)(({ theme }) => ({
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: theme.typography.size.base,
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    minWidth: 80,
}));


const CategorySection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const CategoryHeader = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$accent",
})(({ theme, $accent }) => {
    const accentMap = {
        error: theme.palette.error.main,
        warn: theme.palette.warning.main,
        info: theme.palette.primary.main,
        neutral: theme.palette.text.disabled,
    };
    const accentColor = accentMap[$accent] || theme.palette.text.disabled;
    return {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1.5),
        paddingBottom: theme.spacing(1),
        marginBottom: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: "pointer",
        userSelect: "none",
        "& .cat-collapse": { color: theme.palette.text.secondary },
        "&:hover .cat-collapse": { color: theme.palette.text.primary },
        "& .accent-error": { color: theme.palette.error.main },
        "& .accent-warn": { color: theme.palette.warning.main },
        "& .accent-info": { color: theme.palette.primary.main },
        "& .accent-neutral": { color: theme.palette.text.secondary },
        position: "relative",
        paddingLeft: theme.spacing(1.25),
        "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: "20%",
            bottom: "30%",
            width: 2,
            borderRadius: 1,
            background: accentColor,
        },
    };
});

const CategoryHeading = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "& .MuiSvgIcon-root": {
        fontSize: theme.typography.size.base,
        color: theme.palette.text.secondary,
    },
}));

const CategoryLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: theme.palette.text.primary,
}));

const CategoryCount = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
}));

const CategorySummary = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
    [theme.breakpoints.down("sm")]: { display: "none" },
}));

const CategoryActions = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
}));

const OptionIcon = styled(Person)(({ theme }) => ({
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
}));


const Row = styled(Box, {
    shouldForwardProp: (prop) => !["$rush", "$active", "$muted"].includes(prop),
})(({ theme, $rush, $active, $muted }) => ({
    display: "grid",
    gridTemplateColumns: "auto 1fr auto auto",
    gridTemplateAreas: `
        "priority name due action"
        "priority meta due action"
    `,
    alignItems: "center",
    columnGap: theme.spacing(1.5),
    rowGap: 0,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.shape.borderRadius,
    opacity: $muted ? 0.78 : 1,
    borderLeft: $rush
        ? `3px solid ${theme.palette.error.main}`
        : $active
            ? `3px solid ${theme.palette.primary.main}`
            : "3px solid transparent",
    transition: "background 0.15s ease, opacity 0.15s ease",
    "&:hover": { background: theme.palette.action.hover, opacity: 1 },
    [theme.breakpoints.down("sm")]: {
        gridTemplateAreas: `
            "priority name name action"
            "priority meta due  action"
        `,
    },
}));

const PriorityCol = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    gridArea: "priority",
});

const PriorityStack = styled(Box)({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    lineHeight: 0,
    "& .MuiSvgIcon-root + .MuiSvgIcon-root": { marginTop: -10 },
});

const PriorityDot = styled(Box)(({ theme }) => ({
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: theme.palette.text.disabled,
}));

const NameCol = styled(Box)({
    minWidth: 0,
    gridArea: "name",
});

const NameRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
}));

const RowName = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "$muted",
})(({ theme, $muted }) => ({
    fontSize: theme.typography.size.md,
    fontWeight: $muted ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium,
    color: $muted ? theme.palette.text.secondary : theme.palette.text.primary,
    cursor: "pointer",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "&:hover": {
        color: theme.palette.primary.main,
        textDecoration: "underline",
    },
}));

const RowMeta = styled(Typography)(({ theme }) => ({
    gridArea: "meta",
    minWidth: 0,
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
}));

const MetaLink = styled(RouterLink)(({ theme }) => ({
    color: "inherit",
    textDecoration: "none",
    "&:hover": {
        color: theme.palette.primary.main,
        textDecoration: "underline",
    },
    "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
        borderRadius: theme.shape.borderRadius,
    },
}));

const renderMetaLink = (label, to) =>
    to ? (
        <MetaLink to={to} onClick={(e) => e.stopPropagation()}>
            {label}
        </MetaLink>
    ) : (
        label
    );

const AssignedChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(2),
    fontSize: theme.typography.size.xxs,
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: "0.02em",
    borderColor: theme.palette.divider,
    color: theme.palette.text.secondary,
    flexShrink: 0,
    "& .MuiChip-label": { padding: theme.spacing(0, 0.75) },
}));

const DueCol = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$tone",
})(({ theme, $tone }) => {
    const toneMap = {
        late: theme.palette.error.main,
        today: theme.palette.warning.dark,
        soon: theme.palette.text.primary,
        normal: theme.palette.text.secondary,
    };
    return {
        gridArea: "due",
        fontSize: theme.typography.size.sm,
        fontWeight: $tone === "late" || $tone === "today" ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular,
        color: toneMap[$tone] || toneMap.normal,
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
        [theme.breakpoints.down("sm")]: {
            fontSize: theme.typography.size.xs,
            justifySelf: "end",
        },
    };
});

const HeroDueText = styled("span", {
    shouldForwardProp: (prop) => prop !== "$tone",
})(({ theme, $tone }) => ({
    color: $tone === "late" ? theme.palette.error.main : $tone === "today" ? theme.palette.warning.dark : "inherit",
    fontWeight: $tone === "late" || $tone === "today" ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular,
}));

const ActionCol = styled(Box)(({ theme }) => ({
    gridArea: "action",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
}));

const InlineRunningClock = styled(Box)(({ theme }) => ({
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: theme.typography.size.smd,
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    minWidth: 64,
    textAlign: "right",
}));

const RushBadge = styled(Bolt)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.error.main,
    flexShrink: 0,
}));


const ProjectRow = styled(Box, {
    shouldForwardProp: (prop) => !["$active", "$muted"].includes(prop),
})(({ theme, $active, $muted }) => ({
    display: "grid",
    gridTemplateColumns: "auto 1fr auto auto",
    gridTemplateAreas: `
        "priority name due action"
        "priority meta due action"
    `,
    alignItems: "center",
    columnGap: theme.spacing(1.5),
    rowGap: 0,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.shape.borderRadius,
    opacity: $muted ? 0.78 : 1,
    borderLeft: $active ? `3px solid ${theme.palette.primary.main}` : "3px solid transparent",
    transition: "background 0.15s ease, opacity 0.15s ease",
    "&:hover": { background: theme.palette.action.hover, opacity: 1 },
    [theme.breakpoints.down("sm")]: {
        gridTemplateAreas: `
            "priority name name action"
            "priority meta due  action"
        `,
    },
}));

const ProjectGlyph = styled(FolderIcon)(({ theme }) => ({
    fontSize: theme.typography.size.base,
    color: theme.palette.text.disabled,
}));

const PlainDue = styled(Typography)(({ theme }) => ({
    gridArea: "due",
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    [theme.breakpoints.down("sm")]: {
        fontSize: theme.typography.size.xs,
        justifySelf: "end",
    },
}));


const NestGroup = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
}));

const NestBranch = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(2.5),
    paddingLeft: theme.spacing(1.5),
    borderLeft: `1px solid ${theme.palette.divider}`,
}));

const NestNote = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.disabled,
    padding: theme.spacing(0.5, 0, 0.5, 1.5),
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightSemiBold,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(0.5),
}));

const SectionNote = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    maxWidth: "68ch",
}));

const CollapsibleBar = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    marginTop: theme.spacing(3),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: "pointer",
    userSelect: "none",
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.md,
    "&:hover": { background: theme.palette.action.hover },
}));

const AccountSubheader = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
    padding: theme.spacing(0, 1.5, 0.5),
    marginTop: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));


const AssignedBar = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `3px solid ${theme.palette.text.disabled}`,
    background: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        alignItems: "flex-start",
        gap: theme.spacing(1),
    },
}));

const AssignedBarText = styled(Box)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.secondary,
    "& strong": {
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightSemiBold,
    },
}));

const AssignedToggle = styled(Button)({
    textTransform: "none",
    whiteSpace: "nowrap",
});


const EmptyStateBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
}));

const LoadingBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(8, 2),
    color: theme.palette.text.secondary,
}));

const ErrorBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(6, 2),
    textAlign: "center",
    border: `1px solid ${theme.palette.error.light}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
}));

const RetryButton = styled(Button)({
    textTransform: "none",
});


const PriorityIcon = ({ priority }) => {
    switch (priority) {
        case "Highest":
            return (
                <PriorityStack>
                    <KeyboardArrowUp color="error" fontSize="small" />
                    <KeyboardArrowUp color="error" fontSize="small" />
                </PriorityStack>
            );
        case "High":
            return <KeyboardArrowUp color="warning" fontSize="small" />;
        case "Low":
            return <KeyboardArrowDown color="primary" fontSize="small" />;
        case "Lowest":
            return (
                <PriorityStack>
                    <KeyboardArrowDown color="primary" fontSize="small" />
                    <KeyboardArrowDown color="primary" fontSize="small" />
                </PriorityStack>
            );
        default:
            return <PriorityDot />;
    }
};

const RelationChip = ({ item }) =>
    isResponsible(item) ? null : (
        <Tooltip title="Someone else is responsible for this — you are on the team">
            <AssignedChip label="Assigned" size="small" variant="outlined" />
        </Tooltip>
    );

const TAB_TASKS = "tasks";
const TAB_PROJECTS = "projects";
const TAB_ALL = "all";


const Assignments = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const hasPermission = useHasPermission();
    const [users, setUsers] = useState([]);
    const [activeTimer, setActiveTimer] = useState({});
    const [selectedUser, setSelectedUser] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const [collapsed, setCollapsed] = useState({});
    const [showQuietProjects, setShowQuietProjects] = useState(false);
    const [activeTab, setActiveTab] = useState(TAB_TASKS);
    const [assignedOnly, setAssignedOnly] = useState(false);
    const currentUserId = useSelector((state) => state.auth.userId);
    const timeTracking = useSelector((state) => state.ui.timeTracking);

    const canListAssignments = hasPermission("listAssignments");
    const canListTimers = hasPermission("listTimers");
    const canSeePrivate = hasPermission("canSeePrivate");

    useEffect(() => {
        if (canListAssignments) {
            dispatch(getReportUsers((u) => setUsers(u || [])));
        }
    }, [dispatch, canListAssignments]);

    useEffect(() => {
        setIsLoading(true);
        setLoadFailed(false);
        setAssignedOnly(false);
        dispatch(
            fetchUserAssignments({
                userId: selectedUser ? selectedUser._id : null,
                callback: (data) => {
                    setIsLoading(false);
                    if (data === null || data === undefined) {
                        setLoadFailed(true);
                        setAssignments([]);
                        return;
                    }
                    setAssignments(Array.isArray(data) ? data : []);
                },
            })
        );
    }, [dispatch, selectedUser, reloadToken]);

    useEffect(() => {
        if (canListTimers || selectedUser?._id === currentUserId || selectedUser === null) {
            dispatch(
                getRunningTimerByUser(selectedUser ? selectedUser._id : currentUserId, (data) =>
                    setActiveTimer(data || {})
                )
            );
        }
    }, [dispatch, currentUserId, selectedUser, canListTimers, timeTracking]);

    const visible = useMemo(
        () => (assignedOnly ? assignments.filter((a) => !isResponsible(a)) : assignments),
        [assignments, assignedOnly]
    );

    const { tasks, projects } = useMemo(() => splitByType(visible), [visible]);
    const stats = useMemo(() => buildStats(visible), [visible]);
    const taskBuckets = useMemo(() => bucketTasksByUrgency(tasks), [tasks]);
    const nested = useMemo(() => groupTasksUnderProjects(visible), [visible]);

    const projectSections = useMemo(() => {
        const { responsible, assigned } = splitByRelation(projects);
        return {
            responsible: groupProjectsByAccount(responsible),
            assigned: groupProjectsByAccount(assigned),
        };
    }, [projects]);

    const assignedCounts = useMemo(() => {
        const assigned = assignments.filter((a) => !isResponsible(a));
        const split = splitByType(assigned);
        return { tasks: split.tasks.length, projects: split.projects.length, total: assigned.length };
    }, [assignments]);

    const heroTask = useMemo(() => {
        if (assignedOnly) return null;
        const ordered = sortAssignments(tasks);
        return ordered.find((t) => isResponsible(t)) || ordered[0] || null;
    }, [tasks, assignedOnly]);

    const handleNavigate = useCallback(
        (a) => {
            let path = null;
            if (a.type === "Project") path = projectPath(a.account, a);
            else if (a.type === "Task") path = projectPath(a.account, a.project);
            if (path) navigate(path);
        },
        [navigate]
    );

    const isActiveTimerOn = (id) => !!(activeTimer && activeTimer.parentId === id);
    const canTime = (a) => canListTimers || a.responsible === currentUserId;

    const renderHero = () => {
        const top = heroTask;
        if (!top) return null;
        const active = isActiveTimerOn(top._id);
        return (
            <HeroCard $active={active}>
                <HeroEyebrow>
                    <HeroDot $active={active} />
                    {active ? "Working on this now" : "Work on this next"}
                </HeroEyebrow>
                <HeroTitle onClick={() => handleNavigate(top)}>{top.name}</HeroTitle>
                <HeroMetaRow>
                    <span>Task</span>
                    {top.project?.name && <span>· {renderMetaLink(top.project.name, projectPath(top.account, top.project))}</span>}
                    {top.account?.name && <span>· {renderMetaLink(top.account.name, accountPath(top.account))}</span>}
                    {top.account?.client?.name && (
                        <span>· {renderMetaLink(top.account.client.name, clientPath(top.account.client))}</span>
                    )}
                    {top.dueDate && (
                        <span>
                            ·{" "}
                            <HeroDueText $tone={dueTone(top.dueDate)}>
                                {formatDueLabel(top.dueDate)}
                            </HeroDueText>
                        </span>
                    )}
                    {top.budget?.budget != null && (
                        <span>
                            <AccessTime /> {top.budget.budget || 0}h budget
                        </span>
                    )}
                    {canSeePrivate && top.budget?.quoted != null && (
                        <span>· {top.budget.quoted || 0}h quoted</span>
                    )}
                    {top.rush && (
                        <span style={{ color: theme.palette.error.main, fontWeight: theme.typography.fontWeightMedium }}>
                            <Bolt fontSize="small" /> Rush
                        </span>
                    )}
                    <RelationChip item={top} />
                </HeroMetaRow>
                <HeroActions>
                    {active && (
                        <HeroRunningClock>
                            <RunningTimer startTime={activeTimer.startedAt} />
                        </HeroRunningClock>
                    )}
                    {canTime(top) && (
                        <TimerButton
                            parentId={top._id}
                            parentType={top.type}
                            runningTimerId={active ? activeTimer._id : null}
                        />
                    )}
                </HeroActions>
            </HeroCard>
        );
    };

    const renderMetaParts = (parts) => {
        const shown = parts.filter((part) => part && part.label);
        if (shown.length === 0) return null;
        const title = shown.map((part) => part.label).join(" · ");
        return (
            <RowMeta title={title}>
                {shown.map((part, index) => (
                    <React.Fragment key={part.key}>
                        {index > 0 && " · "}
                        {renderMetaLink(part.label, part.to)}
                    </React.Fragment>
                ))}
            </RowMeta>
        );
    };

    const renderTaskRow = (a, { showProject = false } = {}) => {
        const active = isActiveTimerOn(a._id);
        const muted = !isResponsible(a);
        const meta = [
            showProject ? { key: "project", label: a.project?.name, to: projectPath(a.account, a.project) } : null,
            { key: "account", label: a.account?.name, to: accountPath(a.account) },
            { key: "client", label: a.account?.client?.name, to: clientPath(a.account?.client) },
            { key: "department", label: a.department?.name },
            { key: "budget", label: a.budget?.budget != null ? `${a.budget.budget || 0}h` : null },
        ];
        return (
            <Row key={a._id} $rush={a.rush} $active={active} $muted={muted}>
                <PriorityCol>
                    <PriorityIcon priority={a.priority} />
                </PriorityCol>
                <NameCol>
                    <NameRow>
                        {a.rush && <RushBadge titleAccess="Rush" />}
                        <RowName $muted={muted} onClick={() => handleNavigate(a)} title={a.name}>
                            {a.name}
                        </RowName>
                        <RelationChip item={a} />
                    </NameRow>
                </NameCol>
                {renderMetaParts(meta)}
                <DueCol $tone={dueTone(a.dueDate)}>{formatDueLabel(a.dueDate)}</DueCol>
                <ActionCol>
                    {active && (
                        <InlineRunningClock>
                            <RunningTimer startTime={activeTimer.startedAt} />
                        </InlineRunningClock>
                    )}
                    {canTime(a) && (
                        <TimerButton
                            parentId={a._id}
                            parentType={a.type}
                            runningTimerId={active ? activeTimer._id : null}
                        />
                    )}
                </ActionCol>
            </Row>
        );
    };

    const renderProjectRow = (p) => {
        const active = isActiveTimerOn(p._id);
        const muted = !isResponsible(p);
        const meta = [
            { key: "account", label: p.account?.name, to: accountPath(p.account) },
            { key: "client", label: p.account?.client?.name, to: clientPath(p.account?.client) },
            { key: "status", label: p.status },
        ];
        const due = formatDate(p.dueDate);
        return (
            <ProjectRow key={p._id} $active={active} $muted={muted}>
                <PriorityCol>
                    <ProjectGlyph titleAccess="Project" />
                </PriorityCol>
                <NameCol>
                    <NameRow>
                        <RowName $muted={muted} onClick={() => handleNavigate(p)} title={p.name}>
                            {p.name}
                        </RowName>
                        <RelationChip item={p} />
                    </NameRow>
                </NameCol>
                {renderMetaParts(meta)}
                <PlainDue>{due ? `Due ${due}` : "No due date"}</PlainDue>
                <ActionCol>
                    {active && (
                        <InlineRunningClock>
                            <RunningTimer startTime={activeTimer.startedAt} />
                        </InlineRunningClock>
                    )}
                    {canTime(p) && (
                        <TimerButton
                            parentId={p._id}
                            parentType={p.type}
                            runningTimerId={active ? activeTimer._id : null}
                        />
                    )}
                </ActionCol>
            </ProjectRow>
        );
    };

    const toggleCollapsed = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

    const renderUrgencySection = (key) => {
        const bucket = taskBuckets[key];
        if (!bucket || bucket.length === 0) return null;
        const items = heroTask ? bucket.filter((t) => t._id !== heroTask._id) : bucket;
        if (items.length === 0) return null;
        const isCollapsed = !!collapsed[key];
        const meta = URGENCY_META[key];
        const TypeIcon = meta.icon;
        return (
            <CategorySection key={key}>
                <CategoryHeader
                    $accent={meta.accent}
                    onClick={() => toggleCollapsed(key)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={!isCollapsed}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") toggleCollapsed(key);
                    }}
                >
                    <CategoryHeading>
                        <TypeIcon className={`accent-${meta.accent}`} />
                        <CategoryLabel>{meta.label}</CategoryLabel>
                        <CategoryCount>· {items.length}</CategoryCount>
                    </CategoryHeading>
                    <CategoryActions>
                        <CategorySummary>{meta.hint}</CategorySummary>
                        <Tooltip title={isCollapsed ? "Show these tasks" : "Hide these tasks"}>
                            <IconButton
                                size="small"
                                className="cat-collapse"
                                aria-label={isCollapsed ? "Show these tasks" : "Hide these tasks"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCollapsed(key);
                                }}
                            >
                                {isCollapsed ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowUp fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </CategoryActions>
                </CategoryHeader>
                {!isCollapsed && <Box>{items.map((t) => renderTaskRow(t, { showProject: true }))}</Box>}
            </CategorySection>
        );
    };

    const renderTasksTab = () => {
        if (tasks.length === 0) {
            return (
                <EmptyStateBox>
                    <Typography variant="h6">No open tasks</Typography>
                    <Typography variant="body2">
                        Nothing is waiting on you right now. Check the Projects tab for work that still needs tasks.
                    </Typography>
                </EmptyStateBox>
            );
        }
        return (
            <>
                {renderHero()}
                {URGENCY_ORDER.map(renderUrgencySection)}
            </>
        );
    };

    const renderAccountGroups = (groups) =>
        groups.map((group) => (
            <Box key={group.name}>
                <AccountSubheader>
                    {renderMetaLink(group.name, accountPath(group.account))}
                    {group.account?.client?.name && (
                        <> · {renderMetaLink(group.account.client.name, clientPath(group.account.client))}</>
                    )}
                </AccountSubheader>
                {group.projects.map(renderProjectRow)}
            </Box>
        ));

    const renderProjectsTab = () => {
        if (projects.length === 0) {
            return (
                <EmptyStateBox>
                    <Typography variant="h6">No open projects</Typography>
                    <Typography variant="body2">You are not on any open project right now.</Typography>
                </EmptyStateBox>
            );
        }
        return (
            <>
                <TabIntro>
                    Projects you look after, grouped by the account they belong to. They are not ranked by urgency —
                    open a project to see what its tasks need.
                </TabIntro>
                {renderAccountGroups(projectSections.responsible)}
                {projectSections.assigned.length > 0 && (
                    <>
                        <SectionHeading>Also assigned to you</SectionHeading>
                        <SectionNote>
                            Someone else is responsible for these projects — you were added to the team, so keep an eye
                            on them.
                        </SectionNote>
                        {renderAccountGroups(projectSections.assigned)}
                    </>
                )}
            </>
        );
    };

    const renderAllTab = () => {
        const withTasks = nested.groups.filter((g) => g.tasks.length > 0);
        const quiet = nested.groups.filter((g) => g.tasks.length === 0);
        const assignedQuiet = quiet.filter((g) => !isResponsible(g.project));
        const withoutTasks = quiet.filter((g) => isResponsible(g.project));

        if (nested.groups.length === 0 && nested.ungrouped.length === 0) {
            return (
                <EmptyStateBox>
                    <Typography variant="h6">Nothing assigned to you</Typography>
                    <Typography variant="body2">Work you own or join will show up here.</Typography>
                </EmptyStateBox>
            );
        }

        return (
            <>
                <TabIntro>
                    Everything you hold, with your tasks sitting under the project they belong to.
                </TabIntro>

                {withTasks.map((group) => (
                    <NestGroup key={group.project._id}>
                        {renderProjectRow(group.project)}
                        <NestBranch>{group.tasks.map((t) => renderTaskRow(t))}</NestBranch>
                    </NestGroup>
                ))}

                {nested.ungrouped.length > 0 && (
                    <>
                        <SectionHeading>Tasks in other projects</SectionHeading>
                        <SectionNote>
                            You hold these tasks, but not the project they belong to.
                        </SectionNote>
                        <Box>{nested.ungrouped.map((t) => renderTaskRow(t, { showProject: true }))}</Box>
                    </>
                )}

                {assignedQuiet.length > 0 && (
                    <>
                        <SectionHeading>Also assigned to you</SectionHeading>
                        <SectionNote>
                            Someone else is responsible for these projects — you were added to the team, so keep an eye
                            on them.
                        </SectionNote>
                        <Box>{assignedQuiet.map((group) => renderProjectRow(group.project))}</Box>
                    </>
                )}

                {withoutTasks.length > 0 && (
                    <>
                        <CollapsibleBar
                            role="button"
                            tabIndex={0}
                            aria-expanded={showQuietProjects}
                            onClick={() => setShowQuietProjects((v) => !v)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") setShowQuietProjects((v) => !v);
                            }}
                        >
                            <span>
                                {withoutTasks.length} project{withoutTasks.length === 1 ? "" : "s"} with no task assigned
                                to you
                            </span>
                            {showQuietProjects ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                        </CollapsibleBar>
                        {showQuietProjects && (
                            <Box>
                                {withoutTasks.map((group) => (
                                    <NestGroup key={group.project._id}>
                                        {renderProjectRow(group.project)}
                                        <NestBranch>
                                            <NestNote>No task here is assigned to you.</NestNote>
                                        </NestBranch>
                                    </NestGroup>
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </>
        );
    };

    const assignedInTab =
        activeTab === TAB_TASKS
            ? assignedCounts.tasks
            : activeTab === TAB_PROJECTS
                ? assignedCounts.projects
                : assignedCounts.total;

    const assignedNoun = activeTab === TAB_TASKS ? "task" : activeTab === TAB_PROJECTS ? "project" : "item";

    const renderAssignedBar = () => {
        if (assignedInTab === 0) return null;
        return (
            <AssignedBar>
                <AssignedBarText>
                    <strong>
                        {assignedInTab} {assignedNoun}
                        {assignedInTab === 1 ? "" : "s"} assigned to you
                    </strong>{" "}
                    — someone else is responsible, but you are on the team. They sit after your own work.
                </AssignedBarText>
                <AssignedToggle
                    variant="outlined"
                    size="small"
                    startIcon={<Person />}
                    onClick={() => setAssignedOnly((v) => !v)}
                >
                    {assignedOnly ? "Show everything" : "Show only assigned"}
                </AssignedToggle>
            </AssignedBar>
        );
    };

    const renderBody = () => {
        if (isLoading) {
            return (
                <LoadingBox>
                    <CircularProgress />
                    <Typography variant="body2">Loading your assignments…</Typography>
                </LoadingBox>
            );
        }
        if (loadFailed) {
            return (
                <ErrorBox>
                    <Typography variant="h6" color="error">
                        We could not load your assignments
                    </Typography>
                    <Typography variant="body2">
                        The list did not come back from the server. Try again in a moment.
                    </Typography>
                    <RetryButton
                        variant="outlined"
                        size="small"
                        startIcon={<Refresh />}
                        onClick={() => setReloadToken((n) => n + 1)}
                    >
                        Try again
                    </RetryButton>
                </ErrorBox>
            );
        }
        if (assignments.length === 0) {
            return (
                <EmptyStateBox>
                    <Typography variant="h6">All clear</Typography>
                    <Typography variant="body2">
                        You have no open work right now. Anything you are made responsible for, or added to, shows up
                        here.
                    </Typography>
                </EmptyStateBox>
            );
        }
        const tabBody =
            activeTab === TAB_PROJECTS ? renderProjectsTab() : activeTab === TAB_ALL ? renderAllTab() : renderTasksTab();
        return (
            <>
                {renderAssignedBar()}
                {tabBody}
            </>
        );
    };

    const tabDefs = [
        { value: TAB_TASKS, label: "Tasks", count: stats.tasks },
        { value: TAB_PROJECTS, label: "Projects", count: stats.projects },
        { value: TAB_ALL, label: "All", count: stats.total },
    ];

    const statCards = () => {
        const assignedSub = (count) => (count > 0 ? `${count} assigned to you` : undefined);
        const tasksCard = {
            key: "tasks",
            label: "Tasks",
            value: stats.tasks,
            tone: "neutral",
            icon: <AssignmentIcon fontSize="small" />,
            sub: assignedSub(stats.assignedTasks),
        };
        const projectsCard = {
            key: "projects",
            label: "Projects",
            value: stats.projects,
            tone: "neutral",
            icon: <FolderIcon fontSize="small" />,
            sub: assignedSub(stats.assignedProjects),
        };
        const taskUrgency = [
            {
                key: "overdue",
                label: "Tasks overdue",
                value: stats.overdue,
                tone: stats.overdue > 0 ? "error" : "neutral",
                icon: <WarningIcon fontSize="small" />,
            },
            {
                key: "today",
                label: "Tasks due today",
                value: stats.today,
                tone: stats.today > 0 ? "warn" : "neutral",
                icon: <Today fontSize="small" />,
            },
            {
                key: "rush",
                label: "Rush tasks",
                value: stats.rush,
                tone: stats.rush > 0 ? "error" : "neutral",
                icon: <Bolt fontSize="small" />,
            },
        ];
        if (activeTab === TAB_PROJECTS) {
            return [
                projectsCard,
                {
                    key: "accounts",
                    label: "Accounts",
                    value: stats.accounts,
                    tone: "neutral",
                    icon: <BusinessIcon fontSize="small" />,
                },
            ];
        }
        if (activeTab === TAB_ALL) return [tasksCard, projectsCard, ...taskUrgency];
        return [tasksCard, ...taskUrgency];
    };

    return (
        <ContainerStyled maxWidth="lg">
            <PageHeader
                title="Assignments"
                subtitle="Work you are responsible for, plus work you have been added to."
                actions={canListAssignments ? (
                    <HeaderUserAutocomplete
                        size="small"
                        options={users}
                        value={selectedUser}
                        getOptionLabel={(option) => option.fullName || ""}
                        getOptionKey={(option) => option._id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={selectedUser ? "Viewing assignments for" : "Switch user"}
                                size="small"
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                                <OptionIcon fontSize="small" />
                                {option.fullName}
                            </li>
                        )}
                        onChange={(_, value) => setSelectedUser(value)}
                    />
                ) : null}
            />

            <StatStrip>
                {statCards().map((card) => (
                    <StatCard
                        key={card.key}
                        size="compact"
                        tone={card.tone}
                        label={card.label}
                        value={card.value}
                        sub={card.sub}
                        icon={card.icon}
                    />
                ))}
            </StatStrip>

            <StyledTabs
                value={activeTab}
                onChange={(_, value) => {
                    setActiveTab(value);
                    setAssignedOnly(false);
                }}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Assignment views"
            >
                {tabDefs.map((tab) => (
                    <Tab
                        key={tab.value}
                        value={tab.value}
                        label={
                            <TabLabelBox>
                                {tab.label}
                                {tab.count > 0 && <TabCountChip size="small" label={tab.count} />}
                            </TabLabelBox>
                        }
                    />
                ))}
            </StyledTabs>

            {renderBody()}
        </ContainerStyled>
    );
};

export default Assignments;
